import { CSSProperties, forwardRef, ReactNode, Ref } from 'react';

import { cx } from '@libs/classnames';

import styles from './Table.module.css';

export type TableProps = {
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
  borderSpacing?: 'default' | 'none' | '6px';
};
export function Table({ style, className, children, borderSpacing = 'default' }: TableProps) {
  return (
    <table
      style={style}
      className={cx(
        className,
        styles.table,
        styles[`has_bspacing_${borderSpacing}` as 'has_bspacing_default'],
      )}
    >
      <tbody>{children}</tbody>
    </table>
  );
}

export type TableRowProps = {
  className?: string;
  style?: CSSProperties;
  header?: boolean;
  subHeader?: boolean;
  group?: boolean;
  active?: boolean;
  selected?: boolean;
  clickable?: boolean;
  hoverable?: boolean;
  smallText?: boolean;
  onClick?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
};
export const TableRow = forwardRef(function TableRow(
  {
    className,
    style,
    active,
    group,
    header,
    subHeader,
    selected,
    clickable,
    hoverable,
    smallText,
    onClick,
    onMouseLeave,
    children,
    ...restProps
  }: TableRowProps,
  ref: Ref<HTMLTableRowElement>,
) {
  return (
    <tr
      ref={ref}
      style={style}
      className={cx(className, styles.tableRow, {
        [styles.header]: header,
        [styles.subHeader]: subHeader,
        [styles.group]: group,
        [styles.active]: active,
        [styles.selected]: selected,
        [styles.clickable]: clickable,
        [styles.hoverable]: hoverable,
        [styles.smallText]: smallText,
      })}
      onClick={onClick}
      onPointerLeave={onMouseLeave}
      {...restProps}
    >
      {children}
    </tr>
  );
});

export type TableCellProps = {
  style?: CSSProperties;
  className?: string;
  colSpan?: number;
  right?: boolean;
  left?: boolean;
  top?: boolean;
  centered?: boolean;
  slim?: boolean;
  leftGap?: boolean;
  rightGap?: boolean;
  onClick?: () => void;
  smallText?: boolean;
  fitContent?: boolean;
  children?: ReactNode;
};
export const TableCell = forwardRef(function TableCell(
  {
    style,
    right,
    left,
    top,
    centered,
    slim,
    leftGap,
    rightGap,
    colSpan,
    className,
    onClick,
    smallText,
    fitContent,
    children,
  }: TableCellProps,
  ref: Ref<HTMLTableCellElement>,
) {
  return (
    <td
      ref={ref}
      style={style}
      colSpan={colSpan}
      onClick={onClick}
      className={cx(className, styles.tableCell, {
        [styles.right]: right,
        [styles.left]: left,
        [styles.centered]: centered,
        [styles.top]: top,
        [styles.clickable]: !!onClick,
        [styles.slim]: slim,
        [styles.leftGap]: leftGap,
        [styles.rightGap]: rightGap,
        [styles.smallText]: smallText,
        [styles.fitContent]: fitContent,
      })}
    >
      {children}
    </td>
  );
});
